// classify-agent feed in the left sidebar: what the agent thought, changed and concluded, in order.
// Read-only: the user sends nothing; the only input is a choice on a question card.
import { useEffect, useMemo, useRef, useState } from 'react'
import ClassifyAgentBody, { PrecleanSummary, Timeline } from './ClassifyAgentBody'
import { CLASS_COLORS, QBIQ_LAYER_NAMES, toDisplayName } from './utils'

const CHOOSABLE = QBIQ_LAYER_NAMES.filter(c => c !== 'Untagged')

const ACTIVE = new Set(['running', 'failed'])

// Agent replies use light markdown: **bold** and "- " bullets. Rendered as React text, never as HTML.
function Bold({ text }) {
  return text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith('**') && part.endsWith('**') ? <strong key={i}>{part.slice(2, -2)}</strong> : part)
}

export function Reply({ text }) {
  const blocks = []
  for (const line of (text ?? '').split('\n')) {
    const bullet = line.match(/^\s*[-*] (.*)/)
    const last = blocks[blocks.length - 1]
    if (bullet) {
      if (last?.type === 'ul') last.items.push(bullet[1])
      else blocks.push({ type: 'ul', items: [bullet[1]] })
    } else if (line.trim()) blocks.push({ type: 'p', text: line })
  }
  return (
    <div className="agent-reply">
      {blocks.map((b, i) => b.type === 'ul'
        ? <ul key={i}>{b.items.map((t, j) => <li key={j}><Bold text={t} /></li>)}</ul>
        : <p key={i}><Bold text={b.text} /></p>)}
    </div>
  )
}

function Question({ item, busy, handlers }) {
  const q = item.question
  const handles = useMemo(() => (q.ops ?? []).flatMap(o => o.handles), [q])
  const tag = q.kind === 'tag'
  useEffect(() => {  // a tag question shows its entities as soon as it arrives
    if (tag && !item.answered) handlers.onShowQuestion(handles)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
  if (item.answered)
    return <p className="question-answered">You chose <strong>{item.answered}</strong></p>
  return (
    <div className="question-card">
      {tag && (
        <button className="show-chip" onClick={() => handlers.onShowQuestion(handles)}>
          Show the {handles.length.toLocaleString()} entities
        </button>
      )}
      {q.kind === 'floor_room' && <button className="show-chip" onClick={() => handlers.onShowRoom(item)}>Show {q.id}</button>}
      <div className="question-options">
        {q.options.map(o => (
          <button key={o} className={`question-option ${tag ? 'tag' : ''}`} disabled={busy}
            onClick={() => tag ? handlers.onAnswer(item, o) : handlers.onAnswerText(item, o)}>
            {tag && <span className="agent-dot" style={{ background: CLASS_COLORS[o] }} />}{o}
          </button>
        ))}
        {tag && <button className="question-option skip" disabled={busy} onClick={() => handlers.onAnswer(item, 'Skip')}>Skip</button>}
      </div>
    </div>
  )
}

function AgentMessage({ item, busy, handlers }) {
  const [shown, setShown] = useState(null)
  const toggle = (i, handles) => {
    const next = shown === i ? null : i
    setShown(next)
    handlers.onShow(next === null ? null : handles)
  }
  const actions = (item.steps ?? []).filter(s => s.kind !== 'thought').length
  return (
    <div className="chat-bubble agent">
      {item.reset && <p className="agent-reset">The conversation got too long, so classify-agent started a fresh one (it keeps your corrections).</p>}
      {(item.live || item.steps?.length > 0) && (
        <details className="agent-trace" open={item.live || undefined}>
          <summary>{item.live ? 'Working' : `Thought for ${Math.round(item.seconds ?? 0)} s`} · {actions} step{actions === 1 ? '' : 's'}</summary>
          <Timeline steps={item.steps ?? []} running={!!item.live} startedAt={item.startedAt} />
        </details>
      )}
      {item.kind === 'preclean'
        ? <PrecleanSummary summary={item.precleanSummary} onPreview={handlers.onPreviewPreclean} />
        : <Reply text={item.text} />}
      {item.changes?.length > 0 && (
        <div className="agent-changes">
          <div className="agent-changes-head">
            Applied {item.changes.reduce((n, c) => n + c.count, 0).toLocaleString()} changes
            {item.kept > 0 && <> · kept {item.kept} of your edits</>}
            {item.undone
              ? <span className="agent-hint"> · undone</span>
              : <button className="btn btn-sm agent-undo" onClick={() => handlers.onUndoApply(item)}>Undo all</button>}
          </div>
          {item.changes.map(c => (
            <div key={c.op_id} className="agent-change" title={c.why}>
              <span className="agent-dot" style={{ background: CLASS_COLORS[c.to] }} />
              <button className="agent-change-text" onClick={() => handlers.onShow(c.handles)}>
                {c.layer} <span className="agent-count">{c.count.toLocaleString()}</span>
              </button>
              <select className="agent-class-select" value={c.to} aria-label={`Class for ${c.layer}`}
                disabled={item.undone} onChange={e => handlers.onChangeClass(item, c, e.target.value)}>
                {!CHOOSABLE.includes(c.to) && <option value={c.to}>{c.to}</option>}
                {CHOOSABLE.map(cls => <option key={cls} value={cls}>{toDisplayName(cls)}</option>)}
              </select>
            </div>
          ))}
        </div>
      )}
      {item.highlights?.length > 0 && (
        <div className="show-chips">
          {item.highlights.map((h, i) => (
            <button key={i} className={`show-chip ${shown === i ? 'on' : ''}`} onClick={() => toggle(i, h.handles)}>
              Show {h.label} ({h.handles.length.toLocaleString()})
            </button>
          ))}
        </div>
      )}
      {item.question && <Question item={item} busy={busy} handlers={handlers} />}
      {item.cost != null && (
        <details className="agent-details"><summary>Details</summary>{item.turns} turns · ${item.cost.toFixed(3)}</details>
      )}
    </div>
  )
}

export default function AgentChat({ thread, agent, handlers, onReview, classified,
                                    leftovers = 0, showLeftovers = false, onToggleLeftovers }) {
  const endRef = useRef(null)
  const busy = agent.view === 'running'
  useEffect(() => { endRef.current?.scrollIntoView({ block: 'end' }) }, [thread.length, agent.view])
  return (
    <div className="agent-chat" role="region" aria-label="classify-agent">
      <div className="agent-thread" aria-live="polite">
        {!thread.length && !ACTIVE.has(agent.view) && (
          <p className="agent-empty">
            {classified
              ? 'classify-agent reviews the classification after Classify and applies its fixes. You can undo them here.'
              : 'Classify the plan and classify-agent will review it.'}
          </p>
        )}
        {thread.map(item => <AgentMessage key={item.id} item={item} busy={busy} handlers={handlers} />)}
        {ACTIVE.has(agent.view) && !(agent.view === 'running' && agent.stage === 'floor') && (
          <div className="chat-bubble agent active"><ClassifyAgentBody agent={agent} handlers={handlers} /></div>
        )}
        <span ref={endRef} />
      </div>
      <div className="agent-composer">
        <div className="agent-quick">
          <button disabled={!classified || busy} onClick={onReview}>Review again</button>
          {classified && leftovers > 0 && (
            <button className={showLeftovers ? 'on' : ''} aria-pressed={showLeftovers} onClick={onToggleLeftovers}>
              {showLeftovers ? 'Hide' : 'Show'} leftovers ({leftovers.toLocaleString()})
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
